import type {
  ExecucaoIntegracao,
  Integracao,
  MapaLoja,
  Provedor,
  SalvarIntegracaoParams,
  SugestaoMapa,
} from "@/api/escala.service";

// Mesmo limite do Method (escala_integracao_loja.ref_empresa/ref_filial).
export const MAX_REF_INTEGRACAO = 60;

export interface LinhaMapa {
  loja_id: string;
  empresa: string;
  filial: string;
}

// Uma linha por loja; as que ainda não têm ligação ficam em branco.
export function linhasDoMapa(lojasIds: string[], mapa: MapaLoja[]): LinhaMapa[] {
  const porLoja = new Map(mapa.map((m) => [m.loja_id, m]));
  return lojasIds.map((id) => ({
    loja_id: id,
    empresa: porLoja.get(id)?.empresa ?? "",
    filial: porLoja.get(id)?.filial ?? "",
  }));
}

// Linhas em branco nos dois campos saem do mapa; preencher só um é erro, porque
// a loja ficaria ligada a uma filial que não existe.
export function mapaDasLinhas(linhas: LinhaMapa[]): { mapa: MapaLoja[]; erro?: string } {
  const mapa: MapaLoja[] = [];
  const vistas = new Set<string>();
  for (const l of linhas) {
    const empresa = l.empresa.trim();
    const filial = l.filial.trim();
    if (!empresa && !filial) continue;
    if (!empresa || !filial) return { mapa: [], erro: "Preencha a empresa e a filial, ou deixe os dois em branco." };
    if (empresa.length > MAX_REF_INTEGRACAO || filial.length > MAX_REF_INTEGRACAO) {
      return { mapa: [], erro: `Empresa e filial aceitam no máximo ${MAX_REF_INTEGRACAO} caracteres.` };
    }
    const chave = `${empresa}\u0000${filial}`;
    if (vistas.has(chave)) return { mapa: [], erro: "Duas lojas não podem usar a mesma empresa e filial." };
    vistas.add(chave);
    mapa.push({ loja_id: l.loja_id, empresa, filial });
  }
  return { mapa };
}

// Aplica as sugestões por CNPJ só nas lojas ainda sem ligação: nunca sobrescreve
// o que a pessoa já preencheu. Devolve também quantas foram preenchidas.
export function aplicarSugestoes(linhas: LinhaMapa[], sugestoes: SugestaoMapa[]): { linhas: LinhaMapa[]; aplicadas: number } {
  const porLoja = new Map(sugestoes.filter((s) => s.loja_id).map((s) => [s.loja_id!, s]));
  let aplicadas = 0;
  const out = linhas.map((l) => {
    const s = porLoja.get(l.loja_id);
    if (!s || l.empresa || l.filial) return l;
    aplicadas++;
    return { ...l, empresa: s.empresa, filial: s.filial };
  });
  return { linhas: out, aplicadas };
}

export interface FormIntegracao {
  provedor: string;
  ativa: boolean;
  sincronizar_diariamente: boolean;
  importar_carga_horaria: boolean;
  url: string;
  usuario: string;
  senha: string;
  timeout_seg: number;
  opcoes: Record<string, string>;
}

export const formVazio = (provedor = ""): FormIntegracao => ({
  provedor,
  ativa: false,
  sincronizar_diariamente: false,
  importar_carga_horaria: false,
  url: "",
  usuario: "",
  senha: "",
  timeout_seg: 120,
  opcoes: {},
});

export const formDaIntegracao = (i: Integracao, provedorPadrao: string): FormIntegracao =>
  i.configurada
    ? {
        provedor: i.provedor,
        ativa: i.ativa,
        sincronizar_diariamente: i.sincronizar_diariamente ?? false,
        importar_carga_horaria: i.importar_carga_horaria ?? false,
        url: i.url,
        usuario: i.usuario,
        senha: "",
        timeout_seg: i.timeout_seg,
        opcoes: i.opcoes ?? {},
      }
    : formVazio(provedorPadrao);

// A senha é write-only: o servidor a exige de novo na primeira configuração e
// quando provedor, URL ou usuário mudam (senão a senha antiga iria para outro destino).
export function senhaObrigatoria(f: FormIntegracao, gravada: Integracao): boolean {
  if (!gravada.configurada || !gravada.tem_senha) return true;
  return f.provedor !== gravada.provedor || f.url.trim() !== gravada.url || f.usuario.trim() !== gravada.usuario;
}

// Mesmas checagens de salvar_integracao, que testar_integracao também faz antes de
// chamar o sistema. Devolve a mensagem do primeiro problema, ou null.
export function erroDoForm(f: FormIntegracao, gravada: Integracao, provedor: Provedor | undefined): string | null {
  if (!provedor) return "Escolha o sistema externo";
  if (!f.url.trim()) return "URL é obrigatória";
  if (!f.usuario.trim()) return "Usuário é obrigatório";
  if (!f.senha && senhaObrigatoria(f, gravada)) {
    return gravada.configurada ? "Informe a senha de novo: o sistema, a URL ou o usuário mudou" : "Senha é obrigatória";
  }
  if (!Number.isInteger(f.timeout_seg) || f.timeout_seg < 1 || f.timeout_seg > 600) {
    return "Tempo limite deve ser um número inteiro entre 1 e 600 segundos";
  }
  const faltando = provedor.campos.find((c) => c.obrigatorio && !(f.opcoes[c.chave] ?? "").trim());
  if (faltando) return `${faltando.rotulo} é obrigatório`;
  if (f.importar_carga_horaria && !provedor.carga_horaria) {
    return `${provedor.nome} não informa o horário dos colaboradores: a importação de carga horária não está disponível`;
  }
  return null;
}

export function paramsDoForm(f: FormIntegracao): SalvarIntegracaoParams {
  return { ...f, url: f.url.trim(), usuario: f.usuario.trim() };
}

// Zero filiais é "o sistema não informa" (na Senior, tabela de filiais não liberada
// no web service): a conexão funciona, só não há o que contar.
export function textoTesteOk(mensagem: string, filiais: number): string {
  return filiais > 0 ? `${mensagem} ${filiais} filial(is) encontrada(s).` : mensagem;
}

type Contadores = ExecucaoIntegracao["contadores"];

// "Inseridos" é o que a sincronização criou; "atualizados" o que ela alterou em
// registros que já existiam. A soma e o detalhe da tela saem destas mesmas listas.
const CAMPOS_INSERIDOS: [keyof NonNullable<Contadores>, string][] = [
  ["colaboradores_criados", "Colaboradores criados"],
  ["ausencias_criadas", "Ausências criadas"],
  ["feriados_criados", "Feriados criados"],
  ["cargas_criadas", "Cargas horárias criadas"],
];
const CAMPOS_ATUALIZADOS: [keyof NonNullable<Contadores>, string][] = [
  ["colaboradores_atualizados", "Colaboradores atualizados"],
  ["colaboradores_desativados", "Colaboradores desativados"],
  ["ausencias_canceladas", "Ausências canceladas"],
  ["ausencias_reativadas", "Ausências reativadas"],
  ["ausencias_atualizadas", "Ausências atualizadas"],
  ["feriados_atualizados", "Feriados atualizados"],
  ["colaboradores_carga_alterada", "Cargas horárias trocadas"],
];

export type TipoDetalheExecucao = "inseridos" | "atualizados";

// Linhas "rótulo → quantidade" do que compõe inseridos/atualizados; só o que não é zero.
export function detalheContadores(c: Contadores | undefined, tipo: TipoDetalheExecucao): [string, number][] {
  const campos = tipo === "inseridos" ? CAMPOS_INSERIDOS : CAMPOS_ATUALIZADOS;
  return campos.map(([k, rotulo]): [string, number] => [rotulo, c?.[k] ?? 0]).filter(([, n]) => n > 0);
}

const somar = (linhas: [string, number][]) => linhas.reduce((t, [, n]) => t + n, 0);

export function metricasExecucao(e: Pick<ExecucaoIntegracao, "contadores" | "erros_total">) {
  return {
    inseridos: somar(detalheContadores(e.contadores, "inseridos")),
    atualizados: somar(detalheContadores(e.contadores, "atualizados")),
    erros: e.erros_total,
  };
}

// Duração com milissegundos: "243 ms", "25 s 412 ms", "10 min 7 s 120 ms". Partes zeradas
// à esquerda somem. "" enquanto não concluiu ou se as datas não leem.
export function formatarDuracao(iniciada?: string, concluida?: string): string {
  if (!iniciada || !concluida) return "";
  const total = new Date(concluida).getTime() - new Date(iniciada).getTime();
  if (Number.isNaN(total) || total < 0) return "";
  const min = Math.floor(total / 60000);
  const seg = Math.floor((total % 60000) / 1000);
  const ms = total % 1000;
  if (min > 0) return `${min} min ${seg} s ${ms} ms`;
  if (seg > 0) return `${seg} s ${ms} ms`;
  return `${ms} ms`;
}

// Texto curto do erro de uma execução: a primeira ocorrência e quantas há além dela.
export function mensagemErroExecucao(e: Pick<ExecucaoIntegracao, "erros" | "erros_total">): string {
  const primeira = e.erros[0]?.mensagem;
  if (!primeira) return e.erros_total > 0 ? `${e.erros_total} ocorrência(s)` : "";
  return e.erros_total > 1 ? `${primeira} (+${e.erros_total - 1})` : primeira;
}
